/**
 * Typed fetch client for the PinakesSSO backend.
 *
 * Invariants:
 * - Same-origin; `credentials: 'include'` so the HttpOnly SSO cookie rides along.
 * - The SPA never reads the session cookie and never holds a session token.
 * - Antiforgery (M5): the backend issues a NON-HttpOnly `XSRF-TOKEN` cookie on GET non-`/api`
 *   responses. Mutating calls must echo that cookie's value back in the `X-XSRF-TOKEN` header.
 *   There is no `/antiforgery/token` endpoint — we read the cookie from `document.cookie`.
 * - A 401 from any call means the server no longer considers us authenticated → bounce to /login.
 */

import type {
  AdminUserView,
  AuditEventDto,
  EnrollResult,
  LauncherResult,
  LoginResult,
  LogoutResult,
  MfaMethodDto,
  OkResult,
  PasswordPolicyDto,
  RecoveryCodesResult,
  RecoveryRemaining,
  RedirectAllowlistDto,
  SessionState,
  TotpEnrollResult,
  TwoFactorPolicyDto,
  TwoFactorResult,
  WebAuthnRegisterResult,
} from './types.js';

/**
 * Thrown for non-2xx responses; carries the HTTP status plus, when the backend sent a
 * `{ error, code }` body, the stable machine-readable `code` (see the backend's `ErrorCodes`)
 * so pages can show a specific localized message. `message` is the backend's English text —
 * for branching only, not for display in the localized UI.
 */
export class ApiError extends Error {
  constructor(
    public readonly status: number,
    message?: string,
    public readonly code?: string,
  ) {
    super(message ?? `api ${status}`);
    this.name = 'ApiError';
  }
}

/** Read the non-HttpOnly `XSRF-TOKEN` cookie the backend set on a prior GET. */
function readXsrfToken(): string | null {
  const match = document.cookie.match(/(?:^|;\s*)XSRF-TOKEN=([^;]+)/);
  if (!match) return null;
  try {
    return decodeURIComponent(match[1]);
  } catch {
    // A malformed cookie value (bad %-escape) makes decodeURIComponent throw a URIError; failing
    // safe (no token) lets the request proceed and the server reject it, rather than crashing the call.
    return null;
  }
}

async function request<T>(method: string, url: string, body?: unknown): Promise<T> {
  const headers: Record<string, string> = { Accept: 'application/json' };
  if (body !== undefined) headers['Content-Type'] = 'application/json';
  if (method !== 'GET') {
    const token = readXsrfToken();
    if (token) headers['X-XSRF-TOKEN'] = token;
  }

  const res = await fetch(url, {
    method,
    headers,
    credentials: 'include', // HttpOnly SSO cookie + XSRF-TOKEN cookie ride along (same-origin)
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });

  if (res.status === 401) {
    // Server is authoritative; bounce on unauth. Return a promise that never settles instead of
    // throwing: the redirect IS the outcome, and a throw here would make the calling page flash
    // its own error state during the moments the browser is still loading /login.
    window.location.assign('/login');
    return new Promise<never>(() => {});
  }
  if (!res.ok) {
    // Error responses carry `{ error, code }` (AccountEndpoints et al.) — surface both.
    let error: string | undefined;
    let code: string | undefined;
    try {
      const body = (await res.json()) as { error?: unknown; code?: unknown };
      if (typeof body.error === 'string') error = body.error;
      if (typeof body.code === 'string') code = body.code;
    } catch {
      // Not JSON (proxy error page, empty body) — fall back to status-only.
    }
    throw new ApiError(res.status, error, code);
  }
  // 204 No Content → undefined.
  return res.status === 204 ? (undefined as T) : ((await res.json()) as T);
}

/** Low-level verbs. Pages should prefer the typed `endpoints` below. */
export const api = {
  get: <T>(u: string) => request<T>('GET', u),
  post: <T>(u: string, b?: unknown) => request<T>('POST', u, b),
  put: <T>(u: string, b?: unknown) => request<T>('PUT', u, b),
  del: <T>(u: string, b?: unknown) => request<T>('DELETE', u, b),
};

/**
 * Typed, named endpoints mirroring the backend contract (PinakesSSO.Web/Endpoints).
 * Components call these so URLs and DTOs stay in one place.
 */
export const endpoints = {
  /* --- session / boot --- */
  session: () => api.get<SessionState>('/api/session'),

  /* --- login + 2FA --- */
  loginPassword: (userName: string, password: string, staySignedIn: boolean) =>
    api.post<LoginResult>('/api/login/password', { userName, password, staySignedIn }),
  loginTotp: (code: string) => api.post<TwoFactorResult>('/api/login/totp', { code }),
  loginRecovery: (code: string) => api.post<TwoFactorResult>('/api/login/recovery', { code }),
  loginPasskeyOptions: () => api.post<unknown>('/api/login/passkey/options', {}),
  loginPasskeyVerify: (req: unknown) => api.post<TwoFactorResult>('/api/login/passkey/verify', req),

  /* --- logout --- */
  logout: () => api.post<LogoutResult>('/api/logout', {}),

  /* --- mfa management --- */
  mfaTotpEnroll: () => api.post<TotpEnrollResult>('/api/mfa/totp/enroll', {}),
  mfaTotpConfirm: (code: string, displayName?: string) =>
    api.post<EnrollResult>('/api/mfa/totp/confirm', { code, displayName: displayName ?? null }),
  mfaPasskeyOptions: () => api.post<unknown>('/api/mfa/passkey/options', {}),
  mfaPasskeyVerify: (req: unknown) => api.post<EnrollResult | WebAuthnRegisterResult>('/api/mfa/passkey/verify', req),
  mfaMethods: () => api.get<MfaMethodDto[]>('/api/mfa/methods'),
  mfaRemoveMethod: (id: number) => api.del<void>(`/api/mfa/methods/${id}`),
  mfaRecovery: () => api.get<RecoveryRemaining>('/api/mfa/recovery'),
  mfaRecoveryRegenerate: () => api.post<RecoveryCodesResult>('/api/mfa/recovery/regenerate', {}),

  /* --- account lifecycle --- */
  passwordPolicy: () => api.get<PasswordPolicyDto>('/api/account/password-policy'),
  forgotPassword: (userName: string) =>
    api.post<OkResult>('/api/account/forgot', { userName }),
  resetPassword: (token: string, newPassword: string) =>
    api.post<OkResult>('/api/account/reset', { token, newPassword }),
  // Non-consuming pre-checks so the reset/activate page can show an expired link before asking for a password.
  validateResetToken: (token: string) =>
    api.get<{ valid: boolean }>(`/api/account/reset/validate?token=${encodeURIComponent(token)}`),
  validateActivationToken: (token: string) =>
    api.get<{ valid: boolean }>(`/api/account/activate/validate?token=${encodeURIComponent(token)}`),
  activateAccount: (activationKey: string, password: string) =>
    api.post<OkResult>('/api/account/activate', { activationKey, password }),
  changePassword: (currentPassword: string, newPassword: string) =>
    api.post<OkResult>('/api/account/change-password', { currentPassword, newPassword }),

  /* --- launcher --- */
  launcher: () => api.get<LauncherResult>('/api/launcher'),

  /* --- admin --- */
  adminUserSearch: (q: string) =>
    api.get<AdminUserView[]>(`/api/admin/users?q=${encodeURIComponent(q)}`),
  adminResetPassword: (userType: number, id: number) =>
    api.post<OkResult>('/api/admin/users/reset-password', { userType, id }),
  adminResendActivation: (userType: number, id: number) =>
    api.post<OkResult>('/api/admin/users/resend-activation', { userType, id }),
  adminUnlock: (userType: number, id: number) =>
    api.post<OkResult>('/api/admin/users/unlock', { userType, id }),
  adminResetMfa: (userType: number, id: number) =>
    api.post<OkResult>('/api/admin/users/reset-mfa', { userType, id }),
  adminRegenerateRecovery: (userType: number, id: number) =>
    api.post<RecoveryCodesResult>('/api/admin/users/regenerate-recovery', { userType, id }),
  adminGetPolicy: () => api.get<TwoFactorPolicyDto[]>('/api/admin/policy'),
  adminSavePolicy: (policy: TwoFactorPolicyDto) => api.put<OkResult>('/api/admin/policy', policy),
  adminGetAllowlist: () => api.get<RedirectAllowlistDto[]>('/api/admin/allowlist'),
  adminAddAllowlist: (entry: RedirectAllowlistDto) =>
    api.post<RedirectAllowlistDto>('/api/admin/allowlist', entry),
  adminDeleteAllowlist: (id: number) => api.del<void>(`/api/admin/allowlist/${id}`),
  adminAudit: (query = '') => api.get<AuditEventDto[]>(`/api/admin/audit${query ? `?${query}` : ''}`),
};
